import React, { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Loader2, Lock, Plus, Save, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { StatusChip, resetTabStatusCache } from "./TabStatusControl";

const PALETTE = ["#94A3B8", "#F59E0B", "#10B981", "#EF4444", "#3B82F6", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316", "#334155"];

function ReplaceDialog({ status, count, options, onCancel, onConfirm }) {
  const [rep, setRep] = useState(options[0]?.id || "");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onCancel}>
      <div className="bg-white rounded-lg shadow-xl border border-norea-border w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="status-replace-dialog">
        <h3 className="font-heading text-lg font-medium text-norea-text">Supprimer « {status.label} »</h3>
        <p className="text-xs text-norea-textMuted mt-1">Ce statut est utilisé par <strong>{count}</strong> dossier(s). Choisissez le statut qu'ils prendront à la place (le changement sera noté dans leur historique).</p>
        <select value={rep} onChange={(e) => setRep(e.target.value)} className="input-field text-sm w-full mt-4" data-testid="status-replace-select">
          {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="btn-secondary text-sm" data-testid="status-replace-cancel">Annuler</button>
          <button onClick={() => onConfirm(rep)} disabled={!rep} className="btn-primary text-sm" data-testid="status-replace-confirm">Supprimer et remplacer</button>
        </div>
      </div>
    </div>
  );
}

function StatusRow({ s, i, last, count, onChange, onMove, onRemove }) {
  return (
    <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-3 items-center" data-testid={`status-row-${i}`}>
      <div className="flex flex-col">
        <button onClick={() => onMove(i, -1)} disabled={i === 0} className="text-norea-textMuted disabled:opacity-30" data-testid={`status-up-${i}`} aria-label="Monter"><ArrowUp className="w-3.5 h-3.5" /></button>
        <button onClick={() => onMove(i, 1)} disabled={last} className="text-norea-textMuted disabled:opacity-30" data-testid={`status-down-${i}`} aria-label="Descendre"><ArrowDown className="w-3.5 h-3.5" /></button>
      </div>
      <input value={s.label} onChange={(e) => onChange(i, { label: e.target.value })} className="input-field text-sm" placeholder="Nom du statut" data-testid={`status-label-${i}`} />
      <div className="flex items-center gap-1" data-testid={`status-colors-${i}`}>
        {PALETTE.map((c) => (
          <button key={c} onClick={() => onChange(i, { color: c })} className={`w-4 h-4 rounded-full border-2 ${s.color === c ? "border-norea-text" : "border-transparent"}`} style={{ backgroundColor: c }} aria-label={c} data-testid={`status-color-${i}-${c.slice(1)}`} />
        ))}
      </div>
      <label className="flex items-center gap-1.5 text-xs text-norea-textMuted w-20" title="Statut final : l'onglet est marqué terminé (modifications toujours possibles)">
        <input type="checkbox" checked={!!s.final} onChange={(e) => onChange(i, { final: e.target.checked })} className="accent-norea-primary" data-testid={`status-final-${i}`} /> Final
      </label>
      <span className="text-[10px] text-norea-textMuted tabular-nums w-16 text-right" data-testid={`status-usage-${i}`}>{count || 0} dossier(s)</span>
      {s.system ? (
        <span className="p-2 text-norea-textDisabled" title="Statut protégé (utilisé par le suivi actuel) : renommable, pas supprimable" data-testid={`status-locked-${i}`}><Lock className="w-4 h-4" /></span>
      ) : (
        <button onClick={() => onRemove(i)} className="p-2 text-norea-textMuted hover:text-status-erreurText" aria-label="Supprimer" data-testid={`status-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
      )}
    </div>
  );
}

export default function TabStatusesPanel() {
  const [tabs, setTabs] = useState([]);
  const [tab, setTab] = useState("installation");
  const [items, setItems] = useState([]);
  const [usage, setUsage] = useState({});
  const [replacements, setReplacements] = useState({});
  const [pendingRemove, setPendingRemove] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async (key = tab) => {
    const [{ data }, { data: u }] = await Promise.all([api.get("/configuration/tab-statuses"), api.get(`/configuration/tab-statuses/${key}/usage`)]);
    setTabs(data.tabs);
    setItems((data.tabs.find((t) => t.key === key)?.statuses || []).map((s) => ({ ...s })));
    setUsage(u.counts || {});
    setReplacements({});
    setDirty(false);
  };
  useEffect(() => { load(tab); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const edit = (next) => { setItems(next); setDirty(true); };
  const change = (i, patch) => edit(items.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  const move = (i, d) => { const c = items.slice(); [c[i], c[i + d]] = [c[i + d], c[i]]; edit(c); };
  const remove = (i) => (usage[items[i].id] && items[i].id ? setPendingRemove(i) : edit(items.filter((_, k) => k !== i)));
  const confirmRemove = (rep) => {
    const s = items[pendingRemove];
    setReplacements((r) => ({ ...r, [s.id]: rep }));
    edit(items.filter((_, k) => k !== pendingRemove));
    setPendingRemove(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/configuration/tab-statuses/${tab}`, { statuses: items.map(({ id, label, color, final }) => ({ id, label, color, final })), replacements });
      resetTabStatusCache();
      toast.success(data.moved ? `Statuts enregistrés — ${data.moved} dossier(s) basculé(s)` : "Statuts enregistrés");
      load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-flat p-6" data-testid="panel-tab-statuses">
      <div className="mb-4">
        <h3 className="font-heading text-xl font-medium text-norea-text">Statuts des onglets</h3>
        <p className="text-xs text-norea-textMuted mt-1 max-w-2xl">Définissez les statuts proposés dans chaque onglet du dossier : nom, couleur, ordre et statut « final » (l'onglet est alors marqué terminé). Les 6 statuts de départ (utilisés par la lecture automatique des emails en phase 3) sont protégés : renommables, pas supprimables. Chaque changement est noté dans l'historique de l'onglet.</p>
      </div>
      <div className="mb-5 flex flex-wrap gap-1" data-testid="tab-statuses-tabs">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => !dirty || window.confirm("Abandonner les modifications non enregistrées ?") ? setTab(t.key) : null}
            className={`px-3 py-1.5 text-xs font-medium rounded border transition-colors ${tab === t.key ? "bg-norea-primary text-white border-norea-primary" : "border-norea-border text-norea-textMuted hover:text-norea-text"}`} data-testid={`tab-statuses-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="space-y-2.5">
        {items.map((s, i) => <StatusRow key={s.id || `new-${i}`} s={s} i={i} last={i === items.length - 1} count={usage[s.id]} onChange={change} onMove={move} onRemove={remove} />)}
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button onClick={() => edit([...items, { id: null, label: "Nouveau statut", color: "#3B82F6", final: false }])} className="btn-secondary text-xs" data-testid="btn-add-status"><Plus className="w-3.5 h-3.5" /> Ajouter un statut</button>
        <span className="text-[11px] text-norea-textMuted ml-2">Aperçu :</span>
        {items.map((s, i) => <StatusChip key={s.id || i} status={s} />)}
      </div>
      <div className="mt-5 rounded-md border border-dashed border-norea-border bg-norea-bg px-4 py-3 text-xs text-norea-textMuted flex items-start gap-2" data-testid="status-hook-info">
        <Zap className="w-3.5 h-3.5 mt-0.5 text-norea-primary shrink-0" />
        <span><strong className="text-norea-text">Actions au changement de statut</strong> — chaque changement est déjà enregistré et prêt à déclencher une action automatique (email, WhatsApp, tâche…). Les actions se configureront ici en phase 3.</span>
      </div>
      <div className="mt-6 pt-4 border-t border-norea-border flex items-center justify-end gap-3">
        {dirty && <span className="text-[11px] text-status-incompletText">● Modifications non enregistrées</span>}
        <button onClick={() => load(tab)} disabled={!dirty || saving} className="btn-ghost text-xs" data-testid="btn-tab-statuses-cancel">Annuler</button>
        <button onClick={save} disabled={!dirty || saving} className="btn-primary" data-testid="btn-tab-statuses-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
        </button>
      </div>
      {pendingRemove !== null && (
        <ReplaceDialog status={items[pendingRemove]} count={usage[items[pendingRemove].id]} options={items.filter((s, k) => k !== pendingRemove && s.id)}
          onCancel={() => setPendingRemove(null)} onConfirm={confirmRemove} />
      )}
    </div>
  );
}
